/**
 * Hashes that need the page to settle before being scrolled to, mapped to how
 * long to wait. Keyed without the leading '#'.
 *
 * Empty on purpose. #make-some-magic used to wait 3s here, but the scroll now
 * waits for the lazy-loaded components instead of a fixed guess.
 */
export const DELAYED_HASHES = {};

/** Shared so everything scrolling to a hash target moves the same way. */
export const SCROLL_OPTIONS = { behavior: 'smooth', block: 'start' };

/** How long the DOM must be quiet before the target is treated as settled. */
export const QUIET_MS = 500;

const NOOP = () => {};

/**
 * Scrolls to the element matching window.location.hash, and keeps correcting
 * as the page changes underneath it.
 *
 * Content arriving after the first scroll moves the target, so rather than
 * scrolling once and disconnecting, this re-scrolls on every mutation batch
 * and only stops once the DOM has been quiet for `QUIET_MS`. Gives up after
 * `timeoutMs` if the target never appears, and stops immediately if the
 * visitor scrolls themselves.
 *
 * Returns a teardown function.
 */
export const scrollToHash = (timeoutMs = 10000) => {
  if (typeof window === 'undefined' || !window.location.hash) return NOOP;

  const id = window.location.hash.slice(1);
  if (!id) return NOOP;

  let observer = null;
  let giveUpTimer = null;
  let quietTimer = null;
  let stopped = false;

  const onUserScroll = () => stop();

  function stop() {
    if (stopped) return;
    stopped = true;

    if (observer) observer.disconnect();
    clearTimeout(giveUpTimer);
    clearTimeout(quietTimer);

    window.removeEventListener('wheel', onUserScroll);
    window.removeEventListener('touchstart', onUserScroll);
  }

  const scrollIfPresent = () => {
    const el = document.getElementById(id);
    if (!el) return;

    el.scrollIntoView(SCROLL_OPTIONS);

    // Stop once nothing has moved for a while
    clearTimeout(quietTimer);
    quietTimer = setTimeout(stop, QUIET_MS);
  };

  const start = () => {
    if (stopped) return;

    window.addEventListener('wheel', onUserScroll, { passive: true });
    window.addEventListener('touchstart', onUserScroll, { passive: true });

    scrollIfPresent();

    observer = new MutationObserver(() => {
      if (!stopped) scrollIfPresent();
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });

    giveUpTimer = setTimeout(stop, timeoutMs);
  };

  const delay = DELAYED_HASHES[id];
  if (delay) {
    const delayTimer = setTimeout(start, delay);
    return () => {
      clearTimeout(delayTimer);
      stop();
    };
  }

  start();
  return stop;
};

/**
 * Scrolls on same-page hash navigation (e.g. /page#section while already on
 * /page), which does not re-render the route and so never reaches the entry.
 * Returns a teardown function.
 */
export const listenForHashChange = (timeoutMs = 10000) => {
  if (typeof window === 'undefined') return NOOP;

  let stopPending = NOOP;

  const handler = () => {
    stopPending();
    stopPending = scrollToHash(timeoutMs);
  };

  window.addEventListener('hashchange', handler);

  return () => {
    stopPending();
    window.removeEventListener('hashchange', handler);
  };
};
